import { io, type Socket } from "socket.io-client"

/**
 * One Socket.IO connection per tab.
 *
 * Why Socket.IO and not a plain WebSocket: measured in the customer's network, a
 * raw WebSocket is closed immediately with code 1006, while Socket.IO connects
 * and keeps working — it starts on long polling and only tries to upgrade
 * afterwards. See docs/vorfall-feedback-und-live-ausfall.md.
 *
 * One connection, several lanes: chat, streamed objects and later the live call
 * all travel over this socket, told apart by a request id. Opening one connection
 * per stream would multiply the handshakes, which is what hurts most on polling.
 */
let socket: Socket | null = null

export function getSocket(): Socket {
  if (socket) return socket
  socket = io({
    path: "/api/io",
    transports: ["polling", "websocket"],
    withCredentials: true,
    // Reconnect, but do not queue turns while offline: a chat turn that arrives
    // minutes late would appear under a question nobody remembers asking.
    reconnection: true,
    reconnectionDelayMax: 5_000,
  })
  return socket
}

/**
 * Resolves once the socket is usable, `false` if it is not.
 *
 * Callers use this to decide between the socket and the HTTP route. The timeout
 * is short on purpose: a network that blocks the handshake must not delay the
 * first answer by more than a moment.
 */
export function socketReady(timeoutMs = 2_500): Promise<boolean> {
  const active = getSocket()
  if (active.connected) return Promise.resolve(true)

  return new Promise((resolve) => {
    const done = (value: boolean) => {
      active.off("connect", onConnect)
      active.off("connect_error", onError)
      window.clearTimeout(timer)
      resolve(value)
    }
    const onConnect = () => done(true)
    const onError = () => done(false)

    const timer = window.setTimeout(() => done(false), timeoutMs)
    active.once("connect", onConnect)
    active.once("connect_error", onError)
    if (!active.active) active.connect()
  })
}

/** New request id per stream. Only has to be unique within one connection. */
export function nextRequestId(): string {
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}
